/* product-media-gallery-swiper.js
 * Safe Swiper bootstrap for product media gallery
 * - Swiper CSS/JS already included globally in theme.liquid
 * - Initializes main gallery + vertical thumbnail swiper when visible
 * - Guards against double init
 * - Re-inits on DOM replacement (e.g., variant change)
 * - Exposes window.initProductMediaSwiper(scope)
 */

(function () {
    const MAIN_SEL = '.product__media-list_swiper';
    const THUMBS_SEL = '.product__media-list_thumbs_swiper';
    const DATA_INIT = 'swiperInitialized';
    const DATA_KEY = 'productMediaSwiperInstance';
    const OBS_ATTR = 'data-swiper-observed';

    function raf(fn) {
        if ('requestAnimationFrame' in window) return requestAnimationFrame(fn);
        return setTimeout(fn, 16);
    }

    function idle(fn) {
        if ('requestIdleCallback' in window) return requestIdleCallback(fn, { timeout: 1500 });
        return setTimeout(fn, 1);
    }

    function isElementInDocument(el) {
        return document.contains(el);
    }

    // ---- core init/destroy ---------------------------------------------------

    function createThumbsSwiper(thumbsEl) {
        if (!thumbsEl || thumbsEl.dataset[DATA_INIT] === '1') return null;
        const wrapper = thumbsEl.querySelector('.swiper-wrapper');
        if (!wrapper || !wrapper.children.length) return null;

        const thumbs = new window.Swiper(thumbsEl, {
            direction: 'vertical',
            slidesPerView: 4,
            spaceBetween: 10,
            freeMode: true,
            watchSlidesProgress: true,
            watchSlidesVisibility: true,
            preloadImages: false,
            observer: true,
            observeParents: true,
            touchStartPreventDefault: false,
            passiveListeners: true,
            breakpoints: {
                0: {
                    direction: 'horizontal',
                    slidesPerView: 5,
                    spaceBetween: 8,
                },
                991: {
                    direction: 'vertical',
                    slidesPerView: 4,
                    spaceBetween: 10,
                }
            }
        });

        thumbsEl.dataset[DATA_INIT] = '1';
        thumbsEl[DATA_KEY] = thumbs;
        return thumbs;
    }

    function createMainSwiper(mainEl) {
        if (!window.Swiper || mainEl.dataset[DATA_INIT] === '1') return;

        const wrapper = mainEl.querySelector('.swiper-wrapper');
        if (!wrapper || !wrapper.children.length) return;

        // check if a thumbs swiper exists in the same section/container
        const root = mainEl.closest('.mediaGallery_with_swiper_thumbnails') || document;
        const thumbsEl = root.querySelector(THUMBS_SEL);
        const thumbsInstance = thumbsEl ? (thumbsEl[DATA_KEY] || createThumbsSwiper(thumbsEl)) : null;

        const setThumbsHeight = () => {
            if (thumbsEl) {
                const mainGalleryHeight = mainEl.offsetHeight;
                thumbsEl.style.height = `${mainGalleryHeight}px`;
            }
            if(window.innerWidth < 991){
                thumbsEl.style.height = `auto`;
            }
        };

        const instance = new window.Swiper(mainEl, {
            slidesPerView: 1,
            speed: 400,
            spaceBetween: 0,
            watchOverflow: true,
            preloadImages: false,
            observer: true,
            observeParents: true,
            touchStartPreventDefault: false,
            passiveListeners: true,
            scrollbar: {
                el: ".swiper-scrollbar",
                hide: true,
              },
            navigation: {
                nextEl: mainEl.querySelector('.swiper-button-next-media-gallery'),
                prevEl: mainEl.querySelector('.swiper-button-prev-media-gallery')
            },
            pagination: {
                el: mainEl.querySelector('.swiper-pagination-media-gallery'),
                clickable: true
            },
            thumbs: thumbsInstance ? { swiper: thumbsInstance } : undefined,
            on: {
                init: setThumbsHeight,
                resize: setThumbsHeight,
            },
        });

        mainEl.dataset[DATA_INIT] = '1';
        mainEl[DATA_KEY] = instance;
    }

    function destroySwiper(el) {
        const instance = el && el[DATA_KEY];
        if (instance && typeof instance.destroy === 'function') {
            instance.destroy(true, true);
        }
        if (el) {
            delete el[DATA_KEY];
            delete el.dataset[DATA_INIT];
        }
    }

    // ---- visibility-driven bootstrap ----------------------------------------

    let viewportObserver;
    function observeWhenVisible(el, isThumbs = false) {
        if (el.getAttribute(OBS_ATTR) === '1') return;
        el.setAttribute(OBS_ATTR, '1');

        if (!('IntersectionObserver' in window)) {
            idle(() => raf(() => isElementInDocument(el) && (isThumbs ? createThumbsSwiper(el) : createMainSwiper(el))));
            return;
        }

        viewportObserver =
            viewportObserver ||
            new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const target = entry.target;
                    viewportObserver.unobserve(target);
                    idle(() =>
                        raf(() => {
                            if (!isElementInDocument(target)) return;
                            if (target.matches(THUMBS_SEL)) {
                                createThumbsSwiper(target);
                            } else {
                                createMainSwiper(target);
                            }
                        })
                    );
                });
            }, { rootMargin: '200px 0px', threshold: 0.01 });

        viewportObserver.observe(el);
    }

    // ---- scan + public API ---------------------------------------------------

    function scanAndObserve(scope = document) {
        scope.querySelectorAll(THUMBS_SEL).forEach((el) => observeWhenVisible(el, true));
        scope.querySelectorAll(MAIN_SEL).forEach((el) => observeWhenVisible(el, false));
    }

    function reinitWithin(scope = document) {
        [...scope.querySelectorAll(`${MAIN_SEL}, ${THUMBS_SEL}`)].forEach((el) => {
            if (el.dataset[DATA_INIT] === '1' && !el[DATA_KEY]) {
                delete el.dataset[DATA_INIT];
            }
            if (el.dataset[DATA_INIT] !== '1') {
                observeWhenVisible(el, el.matches(THUMBS_SEL));
            }
        });
    }

    // ---- mutation observer ---------------------------------------------------

    let domObserver;
    function attachDomObserver(root = document) {
        if (!('MutationObserver' in window)) return;

        domObserver =
            domObserver ||
            new MutationObserver((mutations) => {
                let needsRescan = false;

                for (const m of mutations) {
                    m.removedNodes &&
                        m.removedNodes.forEach((n) => {
                            if (!(n instanceof Element)) return;
                            if (n.matches?.(MAIN_SEL) || n.matches?.(THUMBS_SEL)) {
                                destroySwiper(n);
                            }
                            n.querySelectorAll?.(`${MAIN_SEL}, ${THUMBS_SEL}`).forEach(destroySwiper);
                        });

                    if (m.addedNodes && m.addedNodes.length) needsRescan = true;
                    if (m.type === 'attributes' && m.target.matches?.(`${MAIN_SEL}, ${THUMBS_SEL}`)) {
                        needsRescan = true;
                    }
                }

                if (needsRescan) raf(() => reinitWithin(root));
            });

        domObserver.observe(root, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['class', 'style']
        });
    }

    // ---- boot ---------------------------------------------------------------

    function boot() {
        if (!document.querySelector(MAIN_SEL) && !document.querySelector(THUMBS_SEL)) return;
        scanAndObserve(document);
        attachDomObserver(document);
    }

    window.initProductMediaSwiper = function (scope = document) {
        reinitWithin(scope);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { passive: true });
    } else {
        boot();
    }
})();
